// The WIDGET a field is captured with — the `edit_mask` picker plus the small
// panel each mask needs (its value list, or its two checkbox codes).
//
// Extracted from PropertyPanel, which grew it inline, because a document
// repository's metadata field now carries the same `edit_mask` and is rendered
// by the same control at runtime. Two copies of a widget picker is how the two
// screens start offering different masks, and a mask the runtime honours but
// the designer cannot set is a capability nobody finds.
//
// Deliberately NOT a modal: this sits inside whatever panel already holds the
// field, because the mask and its values are one decision and a dialog over
// three text boxes reads as heavier than it is. The repository-only concerns
// (backing a file column with a document repository) stay with their caller —
// see `extra`.

import { Plus, Trash2 } from 'lucide-react'
import type { EditMask, EditMaskValue } from '../txn/types'
// The ONE list of masks: the backend's transaction-model lint reads the same
// file, so a mask name the designer cannot offer is reported, not ignored.
import EDIT_MASKS from './editMasks.json'

/** Every widget a field may declare. `''` is "none" — the control appropriate
 *  to the column's own type. */
export const MASK_TYPES: readonly string[] = EDIT_MASKS.mask_types

/** The masks whose `values` are the point. Everything else keeps an empty list
 *  so switching mask cannot leave orphaned options that render nowhere. */
export const MASKS_WITH_VALUES: readonly string[] = EDIT_MASKS.masks_with_values

const MASK_LABELS: Readonly<Record<string, string>> = EDIT_MASKS.labels

export function maskLabel(m: string): string {
  return MASK_LABELS[m] ?? m.charAt(0).toUpperCase() + m.slice(1)
}

/**
 * What choosing a mask should do to the value list.
 *
 * `bool_check` is the one that needs seeding: it is two FIXED slots (slot 0 is
 * what a tick stores, slot 1 what an untick stores), and an empty pair would
 * save empty strings. Existing values are reused so a round trip through the
 * picker does not wipe what somebody customised.
 */
export function maskForChange(mask: EditMask['mask'],
                              existing: EditMaskValue[] | undefined): EditMask {
  const values = existing || []
  if (mask === 'bool_check') {
    const checked = values[0]?.data ? values[0] : { data: 'Y', display: 'Checked' }
    const unchecked = values[1]?.data ? values[1] : { data: 'N', display: 'Unchecked' }
    return { mask, values: [checked, unchecked] }
  }
  const carries = (MASKS_WITH_VALUES as readonly string[]).includes(mask)
  return { mask, values: carries ? values : [] }
}

export interface EditMaskEditorProps {
  value: EditMask | undefined
  onChange: (mask: EditMask) => void
  /** Host styles, so this looks native in whichever panel mounts it. */
  labelStyle: React.CSSProperties
  inputStyle: React.CSSProperties
  addBtnStyle: React.CSSProperties
  /** Rendered under the picker while the chosen mask matches — used by the
   *  transaction panel for the "back this file column with a document
   *  repository" block, which is a transaction concern, not a widget one. */
  extra?: (mask: string) => React.ReactNode
  label?: string
}

const panelStyle: React.CSSProperties = {
  marginTop: 8, padding: 8,
  background: 'var(--color-bg-secondary)', borderRadius: 4,
}
const panelTitleStyle: React.CSSProperties = {
  fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginBottom: 6,
}
const rowStyle: React.CSSProperties = {
  display: 'flex', gap: 4, marginBottom: 4, alignItems: 'center',
}

export default function EditMaskEditor({
  value, onChange, labelStyle, inputStyle, addBtnStyle, extra,
  label = 'Edit Mask',
}: EditMaskEditorProps) {
  const mask = value?.mask || ''
  const values = value?.values || []

  const setValues = (next: EditMaskValue[]) =>
    onChange({ mask: mask as EditMask['mask'], values: next })

  return (
    <>
      <label style={labelStyle}>{label}</label>
      <select style={{ ...inputStyle, cursor: 'pointer' }} value={mask}
        onChange={(e) => onChange(
          maskForChange(e.target.value as EditMask['mask'], values))}>
        <option value="">None</option>
        {MASK_TYPES.filter((m) => m).map((m) => (
          <option key={m} value={m}>{maskLabel(m)}</option>
        ))}
      </select>

      {extra ? extra(mask) : null}

      {/* bool_check: one checkbox, two fixed slots. Y/N is only the usual
          pair — a column that stores 1/0 or ACTIVE/CLOSED says so here. */}
      {mask === 'bool_check' && (
        <div style={panelStyle}>
          <div style={panelTitleStyle}>Checkbox Value (stored when toggled)</div>
          {[
            { slot: 0, label: 'Checked Value' },
            { slot: 1, label: 'Unchecked Value' },
          ].map(({ slot, label: slotLabel }) => {
            const entry = values[slot] || { data: '', display: '' }
            return (
              <div key={slot} style={rowStyle}>
                <span style={{ flex: '0 0 110px', fontSize: '0.8125rem',
                               color: 'var(--color-text-secondary)' }}>
                  {slotLabel}
                </span>
                <input style={{ ...inputStyle, flex: 1 }} value={entry.data}
                  placeholder="Stored value"
                  onChange={(e) => {
                    const next = [...values]
                    while (next.length < 2) next.push({ data: '', display: '' })
                    next[slot] = { ...next[slot], data: e.target.value }
                    setValues(next.slice(0, 2))
                  }} />
              </div>
            )
          })}
        </div>
      )}

      {/* The value list for the three choice widgets. `data` is stored,
          `display` is read — keeping them apart is what lets a code column
          show a sentence. */}
      {(MASKS_WITH_VALUES as readonly string[]).includes(mask) && (
        <div style={panelStyle}>
          <div style={{ ...panelTitleStyle, marginBottom: 4 }}>
            {maskLabel(mask)} Values (Data / Display)
          </div>
          {values.map((v, vi) => (
            <div key={vi} style={rowStyle}>
              <input style={{ ...inputStyle, flex: 1 }} value={v.data}
                placeholder="Data"
                onChange={(e) => {
                  const next = [...values]
                  next[vi] = { ...next[vi], data: e.target.value }
                  setValues(next)
                }} />
              <input style={{ ...inputStyle, flex: 1 }} value={v.display}
                placeholder="Display"
                onChange={(e) => {
                  const next = [...values]
                  next[vi] = { ...next[vi], display: e.target.value }
                  setValues(next)
                }} />
              <button type="button"
                style={{ background: 'none', border: 'none', cursor: 'pointer',
                         color: 'var(--color-error, #cf222e)', padding: 2 }}
                onClick={() => setValues(values.filter((_, i) => i !== vi))}>
                <Trash2 size={14} />
              </button>
            </div>
          ))}
          <button type="button" style={addBtnStyle}
            onClick={() => setValues([...values, { data: '', display: '' }])}>
            <Plus size={12} /> Add Value
          </button>
        </div>
      )}
    </>
  )
}
